import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { ControlType } from '@/types/floorPlanViewer';

/**
 * Update control scale
 */
export async function updateControlScale(id: string, scale: number): Promise<ControlType> {
  if (scale < 0.2 || scale > 5.0) {
    throw new Error('Scale must be between 0.2 and 5.0');
  }

  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase
    .from('lighting_controls')
    .update({ scale, updated_at: new Date().toISOString() } as any)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    logger.error('Error updating control scale:', error);
    throw error;
  }

  return data as unknown as ControlType;
}

/**
 * Update control rotation
 */
export async function updateControlRotation(id: string, rotation: number): Promise<ControlType> {
  // Normalize rotation to 0-360
  const normalizedRotation = ((rotation % 360) + 360) % 360;

  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase
    .from('lighting_controls')
    .update({ rotation: normalizedRotation, updated_at: new Date().toISOString() } as any)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    logger.error('Error updating control rotation:', error);
    throw error;
  }

  return data as unknown as ControlType;
}

/**
 * Batch update control scales
 */
export async function batchUpdateControlScale(ids: string[], scale: number): Promise<void> {
  if (scale < 0.2 || scale > 5.0) {
    throw new Error('Scale must be between 0.2 and 5.0');
  }

  const supabase = await getAuthenticatedSupabaseClient();
  const { error } = await supabase
    .from('lighting_controls')
    .update({ scale, updated_at: new Date().toISOString() } as any)
    .in('id', ids);

  if (error) {
    logger.error('Error batch updating control scales:', error);
    throw error;
  }
}

/**
 * Get controls by type for batch operations
 */
export async function getControlsByType(
  floorPlanId: string,
  controlType: string
): Promise<ControlType[]> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase
    .from('lighting_controls')
    .select('*')
    .eq('floor_plan_id', floorPlanId)
    .eq('type', controlType);

  if (error) {
    logger.error('Error fetching controls by type:', error);
    throw error;
  }

  return data as unknown as ControlType[];
}
